import { Modal } from '@dynamika-shared/ui/Modal/Modal';
import { useEffect, useState } from 'react';
import { TicketCommentModalProps } from './types';
import { twMerge } from '@dynamika-shared/utils/twMerge';

export const TicketCommentModal: React.FC<TicketCommentModalProps> = ({
  open,
  onCancel,
  onConfirm,
  submitting,
  title,
  messageLines,
  okText = 'OK',
  cancelText = 'Cancel',
}) => {
  const [ticket, setTicket] = useState('');
  const [comment, setComment] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setTicket('');
      setComment('');
      setError(null);
    }
  }, [open]);

  const isTicketEmpty = ticket.trim().length === 0;
  const isOkDisabled = isTicketEmpty || !!submitting;

  const handleOk = () => {
    if (isOkDisabled) return;

    const t = ticket.trim();
    const c = comment.trim();
    if (!t) {
      setError('Ticket is required.');
      return;
    }
    onConfirm({ ticket: t, comment: c || undefined });
  };

  return (
    <Modal
      zIndex={9999}
      width={440}
      open={open}
      onCancel={onCancel}
      onOk={handleOk}
      okText={okText}
      cancelText={cancelText}
      classNames={{
        footer: 'flex items-center justify-between mt-[16px]',
      }}
      okProps={{
        type: 'primary',
        loading: submitting,
        disabled: isOkDisabled,
        className: twMerge(
          'min-w-[16rem] w-[168px] p-2',
          '!rounded-[32px] !border-0',
          isOkDisabled
            ? 'opacity-50 cursor-not-allowed !bg-vividTangelo !text-white'
            : '!bg-vividTangelo hover:!bg-dodgerBlue !text-white hover:!text-white'
        ),
      }}
      cancelProps={{
        className: twMerge(
          'min-w-[16rem] w-[168px] p-2',
          '!rounded-[32px]',
          'bg-dimGray hover:bg-waterLightHover50',
          '!text-black hover:!text-white'
        ),
      }}
      title={title}
    >
      <div className='py-[16px]'>
        {messageLines.map(line => (
          <p key={line}>{line}</p>
        ))}

        <div className='mt-[16px] space-y-3'>
          <div className='space-y-1'>
            <label className='block text-lg font-medium'>
              Ticket <span className='text-redOrange'>*</span>
            </label>
            <input
              type='text'
              className='w-full border border-gray-300 rounded-sm px-2 py-1 text-sm'
              value={ticket}
              onChange={e => {
                setTicket(e.target.value);
                if (error) setError(null);
              }}
            />
          </div>

          <div className='space-y-1'>
            <label className='block text-lg font-medium'>Comment</label>
            <textarea
              className='w-full border border-gray-300 rounded-lg px-2 py-1 text-sm min-h-[80px]'
              value={comment}
              onChange={e => {
                setComment(e.target.value);
                if (error) setError(null);
              }}
            />
          </div>

          {error && <p className='text-lg text-red-600'>{error}</p>}
        </div>
      </div>
    </Modal>
  );
};
